/**
 * AI Radar prompt templates for Word Collection feedback.
 *
 * These prompts enable AI to deliver contextual feedback about:
 * - Rhyme exploration opportunities for unmapped words
 * - Pattern analysis opportunities
 * - Alignment between lyrics and stability settings
 * - Exploration momentum and creative flow
 */

import type { WordCollectionAIContext } from '../../types/wordCollectionAIContext'

/**
 * Rhyme Exploration prompt - suggests which words to phonetically map next.
 *
 * Triggers when: >5 words haven't been explored for rhyme connections
 * Priority: 2 (high value, actionable)
 */
export function buildRhymeExplorationPrompt(context: WordCollectionAIContext): string {
  const verbosity = context.aiFeedbackPreferences?.feedback_verbosity || 'medium'
  const genreContext = getGenreContext(context)

  let prompt = `The songwriter's collection has ${context.totalWordCount} words. ${context.rhymeGroupCount} have been explored for rhyme connections, and ${context.singletonCount} haven't been phonetically mapped yet.`

  if (verbosity === 'low') {
    prompt += `\n\n${genreContext}Suggest 1-2 words to explore for rhyme connections.`
  } else if (verbosity === 'medium') {
    prompt += `\n\n${genreContext}Suggest 2-3 unexplored words from the collection that could unlock interesting rhyme connections. Focus on words that could create strong sonic patterns with existing explored words.`
  } else {
    // high verbosity
    prompt += `\n\n${genreContext}Suggest 2-3 unexplored words from the collection that could unlock interesting rhyme connections. For each, explain what phonetic possibilities it might open up and how those connections could strengthen the song. Focus on natural sonic patterns, not forced rhyming.`
  }

  return prompt
}

/**
 * Pattern Analysis prompt - explores collected word patterns.
 *
 * Triggers when: >10 words in collection
 * Priority: 3 (medium value, exploratory)
 */
export function buildPatternAnalysisPrompt(context: WordCollectionAIContext): string {
  const wordSample = context.recentWords.slice(0, 8).join(', ')
  const verbosity = context.aiFeedbackPreferences?.feedback_verbosity || 'medium'
  const genreContext = getGenreContext(context)

  let prompt = `The songwriter has collected ${context.totalWordCount} words, including: ${wordSample}.`

  if (verbosity === 'low') {
    prompt += `\n\n${genreContext}Identify 1-2 emerging patterns in these words.`
  } else if (verbosity === 'medium') {
    prompt += `\n\n${genreContext}Analyze these words for emerging patterns - semantic themes, emotional tones, or conceptual connections. What does this word collection reveal about where the song might be heading? Suggest 1-2 creative directions to explore based on these patterns.`
  } else {
    // high verbosity
    prompt += `\n\n${genreContext}Analyze these words for emerging patterns - semantic themes, emotional tones, or conceptual connections. What does this word collection reveal about where the song might be heading? Provide detailed analysis of the patterns you observe, and suggest 2-3 creative directions to explore based on these patterns, explaining how each direction could develop the song's themes.`
  }

  return prompt
}

/**
 * Stability Alignment prompt - checks lyrics match stability settings.
 *
 * Triggers when: Lyrics available + stability level set
 * Priority: 2 (high value when both present)
 */
export function buildStabilityAlignmentPrompt(context: WordCollectionAIContext): string {
  // Use AI feedback preference stability if available, otherwise fall back to stabilityLevel
  const stability = context.aiFeedbackPreferences?.stability_preference
    ? context.aiFeedbackPreferences.stability_preference * 10 // Convert 1-10 to percentage
    : context.stabilityLevel || 50
  const stabilityLabel = getStabilityLabel(stability)
  const verbosity = context.aiFeedbackPreferences?.feedback_verbosity || 'medium'
  const genreContext = getGenreContext(context)

  let prompt = `Current lyrics excerpt:
${context.lyrics?.slice(0, 300) || '(No lyrics yet)'}

The songwriter has set stability to "${stabilityLabel}" (${stability}%).`

  if (verbosity === 'low') {
    prompt += ` ${genreContext}Does the word collection align with this stability preference?`
  } else if (verbosity === 'medium') {
    prompt += ` ${genreContext}Evaluate whether the word collection and current lyrics align with this stability preference. Are the collected words supporting the intended tone and coherence level? Suggest any adjustments to better match the stability setting.`
  } else {
    // high verbosity
    prompt += ` ${genreContext}Evaluate whether the word collection and current lyrics align with this stability preference. Analyze how the collected words support or diverge from the intended tone and coherence level. Provide specific examples of alignment or misalignment, and suggest detailed adjustments to better match the stability setting, explaining the reasoning behind each suggestion.`
  }

  return prompt
}

/**
 * Exploration Momentum prompt - encourages continued exploration.
 *
 * Triggers when: Recent thesaurus/rhyme activity detected
 * Priority: 4 (lower priority, motivational)
 */
export function buildExplorationMomentumPrompt(context: WordCollectionAIContext): string {
  const recentActivity = context.lastActivity
    ? `last active ${getRelativeTime(context.lastActivity)}`
    : 'currently exploring'
  const verbosity = context.aiFeedbackPreferences?.feedback_verbosity || 'medium'
  const genreContext = getGenreContext(context)

  let prompt = `The songwriter is actively building their word collection (${context.totalWordCount} words, ${recentActivity}). Exploration depth: ${context.explorationDepth}/10.`

  if (verbosity === 'low') {
    prompt += `\n\n${genreContext}Suggest 1 next step for exploration.`
  } else if (verbosity === 'medium') {
    prompt += `\n\n${genreContext}Recognize their creative momentum and suggest 1-2 next steps to deepen exploration - perhaps diving into thesaurus connections for underexplored words, or finding rhymes for recent additions. Keep the energy flowing.`
  } else {
    // high verbosity
    prompt += `\n\n${genreContext}Recognize their creative momentum and suggest 2-3 next steps to deepen exploration. Consider diving into thesaurus connections for underexplored words, finding rhymes for recent additions, or exploring semantic fields that could enrich the collection. For each suggestion, explain how it could enhance their creative process and strengthen the song. Keep the energy flowing.`
  }

  return prompt
}

/**
 * Creative Suggestions prompt - generic helpful feedback.
 *
 * Triggers when: No other specific triggers active
 * Priority: 5 (lowest priority, fallback)
 */
export function buildCreativeSuggestionsPrompt(context: WordCollectionAIContext): string {
  const topWords = context.topCollectedWords.slice(0, 3).join(', ')
  const verbosity = context.aiFeedbackPreferences?.feedback_verbosity || 'medium'
  const genreContext = getGenreContext(context)

  let prompt = `Word collection status: ${context.totalWordCount} total, ${context.collectedWords} collected, ${context.usedInLyrics} used in lyrics.

Top collected words: ${topWords || 'none yet'}.`

  if (verbosity === 'low') {
    prompt += `\n\n${genreContext}Offer 1 creative suggestion.`
  } else if (verbosity === 'medium') {
    prompt += `\n\n${genreContext}Offer 1-2 gentle creative suggestions to help the songwriter move forward - perhaps exploring connections between collected words, or considering how set-aside words might fit the song's direction.`
  } else {
    // high verbosity
    prompt += `\n\n${genreContext}Offer 2-3 detailed creative suggestions to help the songwriter move forward. Consider exploring connections between collected words, how set-aside words might fit the song's direction, or new angles to approach the song's themes. For each suggestion, explain the potential benefits and how it could strengthen the overall composition.`
  }

  return prompt
}

/**
 * Helper: Get genre context string for prompts
 */
function getGenreContext(context: WordCollectionAIContext): string {
  const prefs = context.aiFeedbackPreferences
  if (!prefs?.genre_context) return ''

  // Use custom genre if specified
  if (prefs.genre_context === 'custom' && prefs.custom_genre) {
    return `In the context of ${prefs.custom_genre} music, `
  }

  // Map genre values to readable labels
  const genreLabels: Record<string, string> = {
    pop: 'pop',
    rock: 'rock',
    country: 'country',
    hip_hop: 'hip-hop',
    rb: 'R&B',
    folk: 'folk',
    indie: 'indie',
    electronic: 'electronic',
    jazz: 'jazz',
    blues: 'blues',
    metal: 'metal',
    punk: 'punk',
    classical: 'classical',
    gospel: 'gospel',
    reggae: 'reggae',
    latin: 'Latin',
  }

  const genreLabel = genreLabels[prefs.genre_context] || prefs.genre_context
  return `In the context of ${genreLabel} music, `
}

/**
 * Helper: Convert stability number to human label
 */
function getStabilityLabel(stability: number): string {
  if (stability < 25) return 'Highly Experimental'
  if (stability < 50) return 'Creative Exploration'
  if (stability < 75) return 'Balanced Development'
  return 'Refinement Focus'
}

/**
 * Helper: Get relative time string (e.g., "2 minutes ago")
 */
function getRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`

  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
}
